import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import PageHeader from '../components/layout/PageHeader';
import Button from '../components/ui/Button';
import { CalendarIcon, UserIcon, TagIcon, ShareIcon, ArrowLeftIcon, ArrowRightIcon, FacebookIcon, TwitterIcon, LinkedinIcon } from 'lucide-react';
import { newsArticles } from '../data/newsArticles';
const NewsDetail = () => {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const [article, setArticle] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [relatedArticles, setRelatedArticles] = useState<any[]>([]);
  useEffect(() => {
    setLoading(true);
    const foundArticle = newsArticles.find(article => article.slug === slug);
    if (foundArticle) {
      setArticle(foundArticle);
      const related = newsArticles.filter(a => a.category === foundArticle.category && a.id !== foundArticle.id).slice(0, 2);
      setRelatedArticles(related);
    }
    setLoading(false);
  }, [slug]);
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  const handleShare = (platform: string) => {
    const url = window.location.href;
    const title = article?.title || 'News from Isonyin Grammar School';
    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${url}&text=${title}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${title}`, '_blank');
        break;
      default:
        navigator.clipboard.writeText(url).then(() => alert('Link copied to clipboard!')).catch(err => console.error('Failed to copy: ', err));
    }
  };
  if (loading) {
    return <div className="min-h-screen bg-white flex items-center justify-center" data-id="element-4269">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-900" data-id="element-4270"></div>
      </div>;
  }
  if (!article) {
    return <div className="min-h-screen bg-white" data-id="element-4271">
        <PageHeader title="Article Not Found" description="The article you are looking for does not exist or has been moved." backgroundImage="https://uploadthingy.s3.us-west-1.amazonaws.com/hgRGwnKLZHVDTRdgag3Y5F/WhatsApp_Image_2025-10-24_at_10.01.11.jpg" breadcrumbs={[{
        label: 'Home',
        path: '/'
      }, {
        label: 'News',
        path: '/news'
      }, {
        label: 'Not Found',
        path: `/news/${slug}`
      }]} data-id="element-4272" />
        <div className="container mx-auto px-4 py-12 text-center" data-id="element-4273">
          <h2 className="text-3xl font-bold text-blue-900 mb-6" data-id="element-4274">
            Article Not Found
          </h2>
          <p className="text-gray-700 mb-8" data-id="element-4275">
            The article you are looking for does not exist or has been moved.
          </p>
          <Button href="/news" variant="primary" icon={<ArrowLeftIcon data-id="element-4277" />} iconPosition="left" data-id="element-4276">
            Back to News
          </Button>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-white" data-id="element-4278">
      <PageHeader title={article.title} description={article.excerpt} backgroundImage={article.image} breadcrumbs={[{
      label: 'Home',
      path: '/'
    }, {
      label: 'News',
      path: '/news'
    }, {
      label: article.title,
      path: `/news/${article.slug}`
    }]} data-id="element-4279" />
      <div className="container mx-auto px-4 py-12" data-id="element-4280">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-4281">
          <div className="lg:col-span-2" data-id="element-4282">
            <article className="bg-white rounded-lg shadow-md p-6 lg:p-8" data-id="element-4283">
              <div className="flex flex-wrap items-center text-gray-500 text-sm mb-6 gap-x-4 gap-y-2" data-id="element-4284">
                <div className="flex items-center" data-id="element-4285">
                  <CalendarIcon className="h-4 w-4 mr-1" data-id="element-4286" />
                  <span data-id="element-4287">{formatDate(article.date)}</span>
                </div>
                <div className="flex items-center" data-id="element-4288">
                  <UserIcon className="h-4 w-4 mr-1" data-id="element-4289" />
                  <span data-id="element-4290">{article.author}</span>
                </div>
                <div className="flex items-center" data-id="element-4291">
                  <TagIcon className="h-4 w-4 mr-1" data-id="element-4292" />
                  <span className="capitalize" data-id="element-4293">{article.category}</span>
                </div>
              </div>
              <div className="prose prose-blue max-w-none mb-8" dangerouslySetInnerHTML={{
              __html: article.content
            }} data-id="element-4294" />
              <div className="border-t border-gray-200 pt-6 mt-8" data-id="element-4295">
                <div className="flex flex-wrap justify-between items-center" data-id="element-4296">
                  <div className="mb-4 md:mb-0" data-id="element-4297">
                    <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-4298">
                      Tags:
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-4299">
                      {article.tags.map((tag: string, index: number) => <Link key={index} to={`/news?tag=${tag}`} className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full text-sm flex items-center transition-colors" data-id="element-4300">
                          <TagIcon className="h-3 w-3 mr-1" data-id="element-4301" />
                          {tag}
                        </Link>)}
                    </div>
                  </div>
                  <div data-id="element-4302">
                    <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-4303">
                      Share:
                    </p>
                    <div className="flex space-x-2" data-id="element-4304">
                      <button onClick={() => handleShare('facebook')} className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" aria-label="Share on Facebook" data-id="element-4305">
                        <FacebookIcon className="h-4 w-4" data-id="element-4306" />
                      </button>
                      <button onClick={() => handleShare('twitter')} className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" aria-label="Share on Twitter" data-id="element-4307">
                        <TwitterIcon className="h-4 w-4" data-id="element-4308" />
                      </button>
                      <button onClick={() => handleShare('linkedin')} className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" aria-label="Share on LinkedIn" data-id="element-4309">
                        <LinkedinIcon className="h-4 w-4" data-id="element-4310" />
                      </button>
                      <button onClick={() => handleShare('copy')} className="p-2 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 transition-colors" aria-label="Copy link" data-id="element-4311">
                        <ShareIcon className="h-4 w-4" data-id="element-4312" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </article>
            <div className="mt-8 flex justify-between" data-id="element-4313">
              <Button href="/news" variant="outline" icon={<ArrowLeftIcon data-id="element-4315" />} iconPosition="left" data-id="element-4314">
                Back to News
              </Button>
              <Button href={`/news/${relatedArticles.length > 0 ? relatedArticles[0].slug : ''}`} variant="outline" icon={<ArrowRightIcon data-id="element-4317" />} iconPosition="right" disabled={relatedArticles.length === 0} data-id="element-4316">
                Next Article
              </Button>
            </div>
          </div>
          <div data-id="element-4318">
            <div className="bg-white rounded-lg shadow-md p-6 mb-6" data-id="element-4319">
              <h3 className="text-xl font-bold text-blue-900 mb-4 border-b border-gray-200 pb-2" data-id="element-4320">
                Related Articles
              </h3>
              {relatedArticles.length > 0 ? <div className="space-y-4" data-id="element-4321">
                  {relatedArticles.map(related => <div key={related.id} className="flex items-start" data-id="element-4322">
                      <img src={related.image} alt={related.title} className="w-20 h-16 object-cover rounded mr-3 flex-shrink-0" data-id="element-4323" />
                      <div data-id="element-4324">
                        <h4 className="font-bold text-blue-900 hover:text-blue-700 transition-colors line-clamp-2" data-id="element-4325">
                          <Link to={`/news/${related.slug}`} data-id="element-4326">
                            {related.title}
                          </Link>
                        </h4>
                        <p className="text-xs text-gray-500 mt-1" data-id="element-4327">
                          {formatDate(related.date)}
                        </p>
                      </div>
                    </div>)}
                </div> : <p className="text-gray-700" data-id="element-4328">No related articles found.</p>}
              <div className="mt-6" data-id="element-4329">
                <Button href="/news" variant="outline" size="sm" className="w-full" data-id="element-4330">
                  View All News
                </Button>
              </div>
            </div>
            <div className="bg-blue-50 rounded-lg border border-blue-100 p-6" data-id="element-4331">
              <h3 className="text-xl font-bold text-blue-900 mb-4" data-id="element-4332">
                Subscribe to Newsletter
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-4333">
                Stay updated with the latest news and events from Isonyin
                Grammar School.
              </p>
              <form className="space-y-3" onSubmit={e => {
              e.preventDefault();
              alert('Subscription successful!');
            }} data-id="element-4334">
                <div data-id="element-4335">
                  <input type="email" placeholder="Your Email Address" className="w-full px-4 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" required data-id="element-4336" />
                </div>
                <Button type="submit" variant="secondary" className="w-full" data-id="element-4337">
                  Subscribe
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default NewsDetail;